// apps/dashboard/src/hooks/useWhiteLabel.js
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { buildQueryParams } from "../lib/globalUtils";

export const DASHBOARD_SITE_TYPE = "dashboard-finitless";
export const DASHBOARD_SITE_QUERY_KEY = ["white-label", "dashboard-site"];

/** Read-only check: returns the project's dashboard-finitless Site, or null. Never provisions. */
export const useDashboardSite = () => {
  const queryString = buildQueryParams({
    where: [{ attribute: "type", value: DASHBOARD_SITE_TYPE }],
  });

  return useQuery({
    queryKey: DASHBOARD_SITE_QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.get(
        `/sites${queryString ? `?${queryString}` : ""}`,
      );
      const sites = response?.result || [];
      return sites[0] || null;
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: false,
  });
};

/** Provisions (or returns) the dashboard-finitless Site via GET /sites/dashboardai/current. */
export const useActivateDashboardSite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.get("/sites/dashboardai/current");
      return response?.result || null;
    },
    onSuccess: (site) => {
      queryClient.setQueryData(DASHBOARD_SITE_QUERY_KEY, site || null);
    },
  });
};

/** Assigns (`{domain}`) or removes (`{domain:null}`) the custom domain via POST /sites/{id}. */
export const useUpdateSiteDomain = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ siteId, domain }) => {
      const response = await apiClient.post(`/sites/${siteId}`, { domain });
      return response?.result || null;
    },
    onSuccess: (site) => {
      if (site) queryClient.setQueryData(DASHBOARD_SITE_QUERY_KEY, site);
    },
  });
};
